/**
 * ч.1089 (вимога власника): попап «перелік прав ролі» на вкладці «Ролі».
 *
 * Показує права ролі людськими назвами з довідника прав, згрупованими за
 * областю обліку (ч.1311, як у редакторі ролі). Право-резерв (`enforced=false` у довіднику —
 * жодне правило його не перевіряє) — сірим з поміткою «поки не діє».
 * Технічні коди клієнту не показуємо (лише у підказці при наведенні).
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { accessRoles, RECORD_SCOPE_LABELS, type AccessRole } from '@/api/admin';
import { PERM_KIND_SUMMARY, permKind } from './permissionKind';
import { groupPermissions } from './permissionGroups';

export default function RolePermissionsDialog({ role, onClose }: {
  role: AccessRole;
  onClose: () => void;
}) {
  const { data: catalog } = useQuery({
    queryKey: ['permissions-catalog'],
    queryFn: () => accessRoles.permissionsCatalog(),
    staleTime: Infinity,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ч.1312: межі записів — назви видів з довідника.
  const { data: scopeKinds } = useQuery({
    queryKey: ['record-scope-kinds'],
    queryFn: () => accessRoles.recordScopeKinds(),
    staleTime: Infinity,
    enabled: Object.keys(role.record_scopes ?? {}).length > 0,
  });
  const narrowed = Object.entries(role.record_scopes ?? {});

  // ч.1311: групи за областю обліку з довідника (як у редакторі ролі).
  const groups = useMemo(() => groupPermissions(role.permissions, catalog), [catalog, role.permissions]);

  // ч.1108: підсумок за видами неперевірюваних прав (системні / в інтерфейсі / поки не діють).
  const kindCounts = (['reserved', 'platform', 'ui'] as const).map((k) => [k, role.permissions.filter(
    (c) => permKind((catalog ?? []).find((x) => x.code === c)) === k).length] as const)
    .filter(([, n]) => n > 0);

  return (
    <div role="dialog" aria-modal="true" aria-label={`Права ролі «${role.name}»`}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 50,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
      }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-panel)', color: 'var(--text)', borderRadius: 'var(--radius)',
        padding: '1.25rem 1.5rem', width: 720, maxWidth: '92vw', maxHeight: '88vh',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0 }}>{role.name}</h3>
            {role.description && (
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                {role.description}
              </div>
            )}
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.35rem' }}>
              Прав: {role.permissions.length}
              {kindCounts.length > 0 && ` (з них ${kindCounts.map(([k, n]) => `${PERM_KIND_SUMMARY[k]}: ${n}`).join(', ')})`}
              {!role.is_system && role.status === 'DEACTIVATED' && ' · роль деактивовано'}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Закрити">✕</button>
        </div>

        {/* ч.1119: права «керування» в ролі перегляду дають лише читання. */}
        {role.is_read_only && (
          <div className="mc__panel mc__panel--info" data-testid="role-read-only-note"
            style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
            Роль лише для перегляду. З цими правами користувач бачить документи, довідники
            і звіти, але не може їх створювати, змінювати, проводити чи видаляти — навіть
            якщо право називається «керування».
          </div>
        )}

        {narrowed.length > 0 && (
          <div className="mc__panel mc__panel--info" data-testid="role-record-scopes-note"
            style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
            Чиї записи бачить користувач:
            <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.2rem' }}>
              {narrowed.map(([code, scope]) => (
                <li key={code}>
                  {scopeKinds?.find((k) => k.code === code)?.label ?? code} — {RECORD_SCOPE_LABELS[scope]}
                </li>
              ))}
            </ul>
            Решта — усі записи організації.
          </div>
        )}

        <div style={{ overflowY: 'auto', marginTop: '0.75rem', paddingRight: '0.25rem' }}>
          {role.permissions.length === 0 && (
            <div style={{ color: 'var(--text-secondary)' }}>У ролі немає жодного права.</div>
          )}
          {!catalog && role.permissions.length > 0 && (
            <div style={{ color: 'var(--text-secondary)' }}>Завантаження…</div>
          )}
          {catalog && groups.map((g) => (
            <section key={g.key} data-testid={`perm-group-${g.key}`}
              style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{g.label}</div>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                {g.perms.map((p) => (
                  <li key={p.code} title={p.code}
                    style={p.note ? { color: 'var(--muted)' } : undefined}>
                    {p.name}{p.note && ` (${p.note})`}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="drawer__actions" style={{ marginTop: '0.75rem' }}>
          <button type="button" className="primary" onClick={onClose}>закрити</button>
        </div>
      </div>
    </div>
  );
}
